import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";

import { css } from "@compiled/react";
import { UniformRichTextNode } from "@uniformdev/canvas-react";
import {
    isRichTextValueConsideredEmpty,
    RichTextNode
} from "@uniformdev/richtext";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";

import type {
    AnimationPhase,
    WebNodeConfig
} from "@/components/adg4/spiderweb-container/src";
import { SpiderWebContainer } from "@/components/adg4/spiderweb-container/src";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

import {
    teamworkGraphAllFramesConnectors,
    teamworkGraphMobileConnectorsA,
    teamworkGraphMobileConnectorsB,
    teamworkGraphMobileConnectorsC,
    teamworkGraphMobileConnectorsD,
    teamworkGraphMobileConnectorsE,
    teamworkGraphMobileConnectorsF,
    teamworkGraphMobileConnectorsH,
    teamworkGraphMobileConnectorsI
} from "../data/connectors";

export interface UniformTeamworkGraphProps {
    frame1Heading?: RichTextNode | { root: RichTextNode };
    frame1Subheading?: RichTextNode | { root: RichTextNode };
    frame2Heading?: string;
    frame2Description?: string;
    frame2VideoUrl?: string;
    frame2VideoImg?: string;
    frame3Heading?: string;
    frame3Description?: string;
    frame3VideoUrl?: string;
    frame3VideoImg?: string;
    frame4Heading?: string;
    frame4Description?: string;
    frame4VideoUrl?: string;
    frame4VideoImg?: string;
    frame5Heading?: string;
    frame5Description?: string;
    frame5VideoUrl?: string;
    frame5VideoImg?: string;
    frame7Heading?: RichTextNode | { root: RichTextNode };
    scrollMagnetAnchors?: number[];
    scrollMagnetMinProgressDelta?: number;
    scrollMagnetDirectionBias?: number;
    stickyHeight?: number;
    onDesktopScrollPhaseProgressChange?: (progress: number) => void;
}

const GRID_IMG =
    "https://dam-cdn.atl.orangelogic.com/AssetLink/857qv237n203wnkea8wgamk2a2us4uy2.png";
const JIRA_IMG =
    "https://dam-cdn.atl.orangelogic.com/AssetLink/jc0417320bg81b53h4sgs70q6l464500.svg";

const textPosition = { x: 22, y: 70 } as const;
const videoPosition = { x: 68, y: 47 } as const;
const vidFrontDimension = 560;

const RECTANGLE_ASSETS_POSITIONS = [
    { x: 105, y: 20 },
    { x: 0, y: 20 }
] as const;

const LOZENGE_POSITIONS = [
    { x: videoPosition.x - 1, y: videoPosition.y + 11 },
    { x: videoPosition.x + 2, y: videoPosition.y + 18 },
    { x: videoPosition.x + 4, y: videoPosition.y + 25 }
] as const;

const LOZENGE_ATTRIBUTES = {
    shape: "rectangle",
    color: "blue600",
    textColor: "white",
    fontFamily: "charlie",
    fontWeight: 400,
    imagePlacement: "outsideLeading",
    size: 40,
    opacity: 0
} as const;

const ICON_SHRUNKEN_SIZE = 38;
const ICON_NORMAL_SIZE = 72;

const AVATAR_SHRUNKEN_SIZE = 100;
const AVATAR_NORMAL_SIZE = 220;

const PILL_SHRUNKEN_SIZE = 45;
const PILL_NORMAL_SIZE = 65;

const FRAME_1_LEAVING_NODES_REF = { x: 32, y: 40 };
const FRAME_1_LEAVING_NODES_ANCHOR_AT_ZOOM = { x: 100, y: 40 };
const FRAME_1_LEAVING_POSITION_SCALE = 2.2;

function frame1NodeLeavingZoomEndPosition(
    nodeX: number,
    nodeY: number
): { x: number; y: number } {
    return {
        x:
            FRAME_1_LEAVING_NODES_ANCHOR_AT_ZOOM.x +
            FRAME_1_LEAVING_POSITION_SCALE *
                (nodeX - FRAME_1_LEAVING_NODES_REF.x),
        y:
            FRAME_1_LEAVING_NODES_ANCHOR_AT_ZOOM.y +
            FRAME_1_LEAVING_POSITION_SCALE *
                (nodeY - FRAME_1_LEAVING_NODES_REF.y)
    };
}

/* frame 1 */
const FRAME_1_ZOOMIN_START = 3;
const FRAME_1_ZOOMIN_END = 9;

const FRAME_1_ADJUST_1_START = 9;
const FRAME_1_ADJUST_1_END = 11;

const FRAME_1_ADJUST_2_START = 11;
const FRAME_1_ADJUST_2_END = 13;

const FRAME_1_ADJUST_3_START = 13;
const FRAME_1_ADJUST_3_END = 15;

/* transition from frame 1 to frame 2 */

const TRANSITION_FROM_FRAME_1_TO_2_START = 15;
const TRANSITION_FROM_FRAME_1_TO_2_END = 19;

/* frame 2 */

const FRAME_2_PULLING_CONTEXT_STEP_1 = 19;
const FRAME_2_PULLING_CONTEXT_STEP_2 = 20;
const FRAME_2_PULLING_CONTEXT_STEP_3 = 21;

const FRAME_2_VIDEO_EXPAND_START = 25;
const FRAME_2_VIDEO_EXPAND_END = 26;

/* transition from frame 2 to frame 3 */
const TRANSITION_FROM_FRAME_2_TO_3_START = 29;
const TRANSITION_FROM_FRAME_2_TO_3_END = 33;

/* frame 3 */

const FRAME_3_PULLING_CONTEXT_STEP_1 = 33;
const FRAME_3_PULLING_CONTEXT_STEP_2 = 34;
const FRAME_3_PULLING_CONTEXT_STEP_3 = 35;

const FRAME_3_VIDEO_EXPAND_START = 39;
const FRAME_3_VIDEO_EXPAND_END = 40;

/* transition from frame 3 to frame 4 */

const TRANSITION_FROM_FRAME_3_TO_4_START = 43;
const TRANSITION_FROM_FRAME_3_TO_4_END = 47;

/* frame 4 */

const FRAME_4_PULLING_CONTEXT_STEP_1 = 47;
const FRAME_4_PULLING_CONTEXT_STEP_2 = 48;
const FRAME_4_PULLING_CONTEXT_STEP_3 = 49;

const FRAME_4_VIDEO_EXPAND_START = 53;
const FRAME_4_VIDEO_EXPAND_END = 54;

/* transition from frame 4 to frame 5 */
const TRANSITION_FROM_FRAME_4_TO_5_START = 57;
const TRANSITION_FROM_FRAME_4_TO_5_END = 61;

/* frame 5 */

const FRAME_5_PULLING_CONTEXT_STEP_1 = 61;
const FRAME_5_PULLING_CONTEXT_STEP_2 = 62;
const FRAME_5_PULLING_CONTEXT_STEP_3 = 63;

const FRAME_5_VIDEO_EXPAND_START = 67;
const FRAME_5_VIDEO_EXPAND_END = 68;

/* transition from frame 5 to frame 6 */
const TRANSITION_FROM_FRAME_5_TO_6_START = 72;
const TRANSITION_FROM_FRAME_5_TO_6_END = 76;

/* frame 6 */
const FRAME_6_ADJUST_1_START = 76;
const FRAME_6_ADJUST_1_END = 79;

const FRAME_6_ADJUST_2_START = 79;
const FRAME_6_ADJUST_2_END = 82;

const FRAME_6_ADJUST_3_START = 82;
const FRAME_6_ADJUST_3_END = 84;

/* transition from frame 6 to frame 7 */
const TRANSITION_FROM_FRAME_6_TO_7_START = 87;
const TRANSITION_FROM_FRAME_6_TO_7_END = 91;

/* frame 7 */

const FRAME_7_ADJUST_1_START = 91;
const FRAME_7_ADJUST_1_END = 92;

const FRAME_7_ADJUST_2_START = 94;
const FRAME_7_ADJUST_2_END = 97;

const FRAME_7_ADJUST_3_START = 97;
const FRAME_7_ADJUST_3_END = 100;

const FRAME_6_NODES_SHRINKING: AnimationPhase = {
    delay: 0,
    duration: 0,
    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
    width: 1,
    height: 1,
    size: 1,
    opacity: 0,
    x: 18.6,
    y: 74.9
};

const ZOOMED_IN_NODES_SHIFTING_LEFT: AnimationPhase = {
    delay: 0,
    duration: 0,
    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
    x: -150
};

const FRAME_2_NODES_SHIFTING_UP_LEFT: AnimationPhase = {
    delay: 0,
    duration: 0,
    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
    x: -50,
    y: -50
};

const FRAME_3_NODES_SHIFTING_DOWN_LEFT: AnimationPhase = {
    delay: 0,
    duration: 0,
    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
    x: -50,
    y: 150
};

const FRAME_4_NODES_SHIFTING_UP_RIGHT: AnimationPhase = {
    delay: 0,
    duration: 0,
    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
    x: 150,
    y: -50
};

const FRAME_5_NODES_SHIFTING_LEFT: AnimationPhase = {
    delay: 0,
    duration: 0,
    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
    x: -150
};

const MOBILE_TEAMWORK_GRAPH_WIDTH = 768;
const MOBILE_TEAMWORK_GRAPH_HEIGHT_SCALE = MOBILE_TEAMWORK_GRAPH_WIDTH / 440;
const scaledMobileTeamworkGraphHeight = (heightAt440: number) =>
    Math.round(heightAt440 * MOBILE_TEAMWORK_GRAPH_HEIGHT_SCALE);
const mobileHeadingCss = { fontSize: "4rem" } as const;
const mobileDescriptionCss = { fontSize: "3rem" } as const;

const desktopHeadingCss = { fontSize: "2rem" } as const;
const desktopDescriptionCSS = { fontSize: "1.5rem" } as const;

const stickyWrapperCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
    maxWidth: "100%",
    position: "sticky",
    margin: "0 auto",
    insetBlockStart: "10vh",
    insetInlineStart: 0
});

const navigationContainerCss = css({
    alignItems: "center",
    position: "absolute",
    zIndex: 3,
    margin: "auto",
    insetBlockStart: "80vh",
    insetBlockEnd: "auto",
    insetInlineStart: 0,
    insetInlineEnd: 0,
    width: "60%",
    display: "flex",
    gap: token("space.100"),
    padding: `${token("space.100")} ${token("space.400")}`
});

const progressTimelineCss = css({
    position: "relative",
    width: "100%",
    height: "8px",
    flexGrow: 1,
    pointerEvents: "none",
    zIndex: 2,
    overflow: "hidden",
    borderRadius: wacToken("corner.radius.pill")
});

const skipButtonCss = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    cursor: "pointer",
    display: "inline-block",
    color: wacToken("color.neutral.light0"),
    border: "1px solid",
    borderRadius: wacToken("corner.radius.pill"),
    padding: `${token("space.100")} ${token("space.400")}`,
    fontFamily: wacToken("font.charlie.display"),
    fontSize: token("space.150"),
    lineHeight: 1,
    ["&:hover"]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "rgba(100, 100, 100, 0.7)"
    }
});

const barsCss = css({
    width: "100%",
    height: "8px",
    position: "absolute",
    display: "flex",
    flexDirection: "row",
    gap: "8px"
});

const barsBehindCss = css({
    backgroundColor: wacToken("color.neutral.light900")
});

const barsFrontCss = css({
    backgroundColor: wacToken("color.primary.blue")
});

const gradientOverlayStyle = css({
    "position": "absolute",
    "inset": 0,
    "width": "100%",
    "height": "90vh",
    "pointerEvents": "none",
    "zIndex": 2,
    "@media (min-width: 1920px)": {
        "&:before, &:after": {
            content: "",
            position: "absolute",
            insetBlockStart: 0,
            insetInlineStart: 0,
            width: "10vw",
            height: "100%",
            pointerEvents: "none",
            zIndex: 2
        },
        "&:before": {
            insetInlineStart: 0,
            insetInlineEnd: "auto",
            backgroundImage: `linear-gradient(to right, ${wacToken(
                "color.primary.black"
            )}, transparent)`
        },
        "&:after": {
            insetInlineEnd: 0,
            insetInlineStart: "auto",
            backgroundImage: `linear-gradient(to left, ${wacToken(
                "color.primary.black"
            )}, transparent)`
        }
    }
});

const teamworkGraphDesktopOnlyStyles = css({
    "position": "relative",
    "@media (max-width: 767px)": {
        display: "none"
    },
    ["&.desktop_container_docked"]: {
        height: "100vh",
        overflow: "hidden"
    }
});

const teamworkGraphMobileOnlyStyles = css({
    "display": "none",
    "@media (max-width: 767px)": {
        display: "block"
    }
});

const teamworkGraphFrameCopyDefaultMessages = defineMessages({
    frame2Heading: {
        id: "teamworkGraph.frame2.heading.default",
        defaultMessage: "With shared data from everywhere"
    },
    frame2Description: {
        id: "teamworkGraph.frame2.description.default",
        defaultMessage:
            "Connect data from every source to bring context to decisions, ideas, and workflows"
    },
    frame3Heading: {
        id: "teamworkGraph.frame3.heading.default",
        defaultMessage: "Connected to impact"
    },
    frame3Description: {
        id: "teamworkGraph.frame3.description.default",
        defaultMessage:
            "Teams and AI operate with awareness of priorities and time progress."
    },
    frame4Heading: {
        id: "teamworkGraph.frame4.heading.default",
        defaultMessage: "In sync across every team"
    },
    frame4Description: {
        id: "teamworkGraph.frame4.description.default",
        defaultMessage:
            "Shared context means AI outputs are relevant and you never miss a beat."
    },
    frame5Heading: {
        id: "teamworkGraph.frame5.heading.default",
        defaultMessage: "Getting it 'Done' faster"
    },
    frame5Description: {
        id: "teamworkGraph.frame5.description.default",
        defaultMessage:
            "When teams are aligned and context-aware, they tap into Rovo to get their work done faster, with confidence."
    }
});

export type DesktopScrollPhase = "track" | "docked";

const TEAMWORK_GRAPH_SKIP_SCROLL_MS = 1600;
const TEAMWORK_GRAPH_SKIP_SCROLL_TOP_PADDING_PX = 16;
export const DEFAULT_SCROLL_MAGNET_ANCHORS = [
    0,
    FRAME_1_ZOOMIN_START,
    FRAME_1_ZOOMIN_END,
    FRAME_1_ADJUST_3_END,
    TRANSITION_FROM_FRAME_1_TO_2_END,
    FRAME_2_PULLING_CONTEXT_STEP_3,
    FRAME_2_VIDEO_EXPAND_END,
    TRANSITION_FROM_FRAME_2_TO_3_END,
    FRAME_3_PULLING_CONTEXT_STEP_3,
    FRAME_3_VIDEO_EXPAND_END,
    TRANSITION_FROM_FRAME_3_TO_4_END,
    FRAME_4_PULLING_CONTEXT_STEP_3,
    FRAME_4_VIDEO_EXPAND_END,
    TRANSITION_FROM_FRAME_4_TO_5_END,
    FRAME_5_PULLING_CONTEXT_STEP_3,
    FRAME_5_VIDEO_EXPAND_END,
    TRANSITION_FROM_FRAME_5_TO_6_END,
    FRAME_6_ADJUST_1_END,
    FRAME_6_ADJUST_3_END,
    TRANSITION_FROM_FRAME_6_TO_7_END,
    FRAME_7_ADJUST_2_END,
    100
];

const exportableDefaultScrollMagnetAnchors = [...DEFAULT_SCROLL_MAGNET_ANCHORS];
export { exportableDefaultScrollMagnetAnchors };

function easeInOutCubicSkipScroll(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/* eslint-disable complexity */
const UniformTeamworkGraph = (props: UniformTeamworkGraphProps) => {
    const {
        frame1Heading,
        frame1Subheading,
        frame2Heading,
        frame2Description,
        frame2VideoUrl,
        frame2VideoImg,
        frame3Heading,
        frame3Description,
        frame3VideoUrl,
        frame3VideoImg,
        frame4Heading,
        frame4Description,
        frame4VideoUrl,
        frame4VideoImg,
        frame5Heading,
        frame5Description,
        frame5VideoUrl,
        frame5VideoImg,
        frame7Heading,
        // scrollMagnetAnchors = DEFAULT_SCROLL_MAGNET_ANCHORS,
        // scrollMagnetMinProgressDelta,
        // scrollMagnetDirectionBias,
        stickyHeight = 1950,
        onDesktopScrollPhaseProgressChange
    } = props;

    // const orderedScrollMagnetAnchors = [...scrollMagnetAnchors].sort(
    //     (left, right) => left - right
    // );

    const isSafariBrowser =
        typeof navigator !== "undefined" &&
        /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

    const bgImageSafari = (src: string) => {
        if (isSafariBrowser) {
            return src;
        }
        return undefined;
    };
    const htmlVideoSafari = (src: string) => {
        if (isSafariBrowser) {
            return null;
        }
        return (
            <video
                style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    borderRadius: "10px",
                    pointerEvents: "none"
                }}
                src={src}
                autoPlay
                muted
                loop
                playsInline
            />
        );
    };

    const intl = useIntl();

    const frame2HeadingWithDefault =
        frame2Heading ||
        intl.formatMessage(teamworkGraphFrameCopyDefaultMessages.frame2Heading);
    const frame2DescriptionWithDefault =
        frame2Description ||
        intl.formatMessage(
            teamworkGraphFrameCopyDefaultMessages.frame2Description
        );
    const frame3HeadingWithDefault =
        frame3Heading ||
        intl.formatMessage(teamworkGraphFrameCopyDefaultMessages.frame3Heading);
    const frame3DescriptionWithDefault =
        frame3Description ||
        intl.formatMessage(
            teamworkGraphFrameCopyDefaultMessages.frame3Description
        );
    const frame4HeadingWithDefault =
        frame4Heading ||
        intl.formatMessage(teamworkGraphFrameCopyDefaultMessages.frame4Heading);
    const frame4DescriptionWithDefault =
        frame4Description ||
        intl.formatMessage(
            teamworkGraphFrameCopyDefaultMessages.frame4Description
        );
    const frame5HeadingWithDefault =
        frame5Heading ||
        intl.formatMessage(teamworkGraphFrameCopyDefaultMessages.frame5Heading);
    const frame5DescriptionWithDefault =
        frame5Description ||
        intl.formatMessage(
            teamworkGraphFrameCopyDefaultMessages.frame5Description
        );

    const pullingInContextString = intl.formatMessage({
        id: "teamworkGraph.framesAl.pullingInContext",
        defaultMessage: "Pulling in context..."
    });

    const teamworkGraphMobileNodesA: WebNodeConfig[] = [
        {
            id: "mobile-component-A-grid-left",
            x: 30,
            y: 0,
            width: 800,
            height: 1600,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/7862q04hkv188isqi7vuls055j6f64fq.png"
        },

        {
            id: "mobile-component-A-dot-top-left",
            x: 3,
            y: -2,
            shape: "circle",
            bgColor: "transparent",
            size: 1
        },
        {
            id: "mobile-component-A-dot-top-right",
            x: 101,
            y: 9,
            shape: "circle",
            bgColor: "transparent",
            size: 1
        },
        {
            id: "mobile-component-A-dot-right-bottom",
            x: 101,
            y: 89,
            shape: "circle",
            bgColor: "transparent",
            size: 1
        },
        {
            id: "mobile-component-A-avatar-blue-top",
            x: 50,
            y: 50,
            shape: "circle",
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/vvs64iqhos5vy1u702ok4u50s42m10ii.png",
            size: 425
        },
        {
            id: "mobile-component-A-icon-figma-top",
            x: 70,
            y: 0,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg"
        },
        {
            id: "mobile-component-A-rectangle-project",
            x: 3,
            y: 13,
            shape: "rectangle",
            bgColor: "black",
            borderColor: "blue600",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.project",
                defaultMessage: "Project"
            }),
            textColor: "white",
            size: 130
        },
        {
            id: "mobile-component-A-hex-wrench-1",
            x: 20,
            y: 26,
            shape: "hexagon",
            size: 70,
            bgColor: "blue600",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/x6bd18r1i1qx1k7xt61pbnnskyf76y4r.svg"
        },
        {
            id: "mobile-component-A-hex-wrench-2",
            x: 80,
            y: 33,
            shape: "hexagon",
            size: 70,
            bgColor: "blue600",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/x6bd18r1i1qx1k7xt61pbnnskyf76y4r.svg"
        },

        {
            id: "mobile-component-A-icon-circle-arrow",
            x: 97,
            y: 52,
            size: 130,
            shape: "rectangle",
            bgColor: "black",
            borderColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/6k3dp10ut224t4oa4ih2ns74gx2r07yh.svg"
        },
        {
            id: "mobile-component-A-icon-microsoft-teams",
            x: 3,
            y: 50,
            size: 130,
            shape: "rectangle",
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FS.png"
        },
        {
            id: "mobile-component-A-rectangle-prompt",
            borderColor: "rainbowRovo",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/82b230b227f1f2nass7sp15dt05ib3ni.jpg",
            x: 50,
            y: 88,
            width: 800,
            height: 285,
            shape: "rectangle"
        }
    ];

    const teamworkGraphMobileNodesB: WebNodeConfig[] = [
        {
            id: "mobile-component-B-dot-top",
            x: 50,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-B-dot-left-bottom",
            x: -1,
            y: 40,
            shape: "circle",
            bgColor: "transparent",
            size: 1
        },
        {
            id: "mobile-component-B-dot-right-bottom",
            x: 101,
            y: 89,
            shape: "circle",
            bgColor: "transparent",
            size: 1
        },
        {
            id: "mobile-component-B-grid-right",
            x: 60,
            y: 50,
            width: 800,
            height: 1600,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/58tq64t0d72pjl8y7cgijgl8ag5a23tf.png"
        },
        {
            id: "mobile-component-B-icon-confluence",
            x: 50,
            y: 7,
            size: 130,
            shape: "rectangle",
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/i8rco43kk1s7kmr72f303wo4esw3s57j.svg"
        },
        {
            id: "mobile-component-B-icon-figma-bottom",
            x: 87,
            y: 43,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg"
        },
        {
            id: "mobile-component-B-lozenge-marketing-veronica",
            x: 55,
            y: 54,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.marketingVeronica",
                defaultMessage: "Marketing / Veronica"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-B-lozenge-meeting-recordings",
            x: 50,
            y: 62,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.meetingRecordings",
                defaultMessage: "Meeting Recordings"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-B-lozenge-campaign-ad-refresh",
            x: 45,
            y: 70,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.campaignAdRefresh",
                defaultMessage: "Campaign Ad Refresh"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-B-rectangle-assets",
            x: 37,
            y: 87,
            width: 700,
            height: 400,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/ycp11j6s2wt83bh3wy7713h1r85mv8ex.png"
        }
    ];

    const teamworkGraphMobileNodesC: WebNodeConfig[] = [
        {
            id: "mobile-component-C-dot-top",
            x: 10,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-C-icon-figma-bottom",
            x: 12,
            y: 91,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg"
        },

        {
            id: "mobile-component-C-dot-left",
            x: -2,
            y: 15,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-C-frame-text",
            x: 50,
            y: 50,
            shape: "rectangle",
            bgColor: "transparent",
            width: 700,
            height: 800,
            textColor: "white",
            fontFamily: "charlie",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={mobileHeadingCss}>{frame2HeadingWithDefault}</h3>
                    <p style={mobileDescriptionCss}>
                        {frame2DescriptionWithDefault}
                    </p>
                </div>
            )
        }
    ];

    const teamworkGraphMobileNodesD: WebNodeConfig[] = [
        {
            id: "mobile-component-D-dot-top",
            x: 15,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-D-dot-right",
            x: 101,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-D-icon-gdrive",
            x: 80,
            y: 10,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/x6fti424cg1dyn52m750hns8386vyw2o.svg"
        },
        {
            id: "mobile-component-D-lozenge-strategy-brief",
            x: 45,
            y: 35,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.strategyBrief",
                defaultMessage: "Strategy Brief"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-D-lozenge-portal-repo-20",
            x: 50,
            y: 45,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalRepo2",
                defaultMessage: "Portal Repo 2.0"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-D-lozenge-okr",
            x: 65,
            y: 55,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.okrAdd300kAccounts",
                defaultMessage: "[01.KR2] Add 300k accounts"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-D-rectangle-in-progress",
            x: 62,
            y: 84,
            width: 700,
            height: 400,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/q2408086s36q3g576cw1b2042ni4t57c.png"
        }
    ];

    const teamworkGraphMobileNodesE: WebNodeConfig[] = [
        {
            id: "mobile-component-E-dot-top",
            x: 80,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-E-dot-left",
            x: 0,
            y: 80,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-E-icon-sharepoint-bottom",
            x: 80,
            y: 91,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EWOC.svg"
        },
        {
            id: "mobile-component-E-frame-text",
            x: 50,
            y: 50,
            shape: "rectangle",
            bgColor: "transparent",
            width: 700,
            height: 800,
            textColor: "white",
            fontFamily: "charlie",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={mobileHeadingCss}>{frame3HeadingWithDefault}</h3>
                    <p style={mobileDescriptionCss}>
                        {frame3DescriptionWithDefault}
                    </p>
                </div>
            )
        }
    ];

    const teamworkGraphMobileNodesF: WebNodeConfig[] = [
        {
            id: "mobile-component-F-dot-top",
            x: 80,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-F-dot-left",
            x: 0,
            y: 20,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-F-lozenge-portal-redesign-brainstorm",
            x: 58,
            y: 34,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalRedesignBrainstorm",
                defaultMessage: "Portal Redesign Brainstorm"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-F-lozenge-portal-designs",
            x: 65,
            y: 44,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalDesigns",
                defaultMessage: "Portal Designs"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-F-lozenge-okr",
            x: 50,
            y: 54,
            text: intl.formatMessage({
                id: "teamworkGraph.mobile.frameFokrLabel",
                defaultMessage: "[O1.KR2] Add 300k accounts"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-F-rectangle-comment",
            x: 42,
            y: 84,
            width: 700,
            height: 400,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/2gf42uyw6x35g346v1337b15dx434t0a.png"
        },
        {
            id: "mobile-component-F-dot-bottom-right",
            x: 0,
            y: 100,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        }
    ];

    const teamworkGraphMobileNodesG: WebNodeConfig[] = [
        {
            id: "mobile-component-G-frame-text",
            x: 50,
            y: 50,
            shape: "rectangle",
            bgColor: "transparent",
            width: 700,
            height: 800,
            textColor: "white",
            fontFamily: "charlie",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={mobileHeadingCss}>{frame4HeadingWithDefault}</h3>
                    <p style={mobileDescriptionCss}>
                        {frame4DescriptionWithDefault}
                    </p>
                </div>
            )
        }
    ];

    const teamworkGraphMobileNodesH: WebNodeConfig[] = [
        {
            id: "mobile-component-H-dot-top-left",
            x: -5,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-H-dot-top-right",
            x: 111,
            y: 0,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-H-dot-middle-left",
            x: -20,
            y: 60,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-H-icon-figma",
            x: 70,
            y: 18,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg"
        },
        {
            id: "mobile-component-H-icon-microsoft",
            x: 14,
            y: 30,
            size: 130,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/72jyk7b1r48ul3cb6qusr6b1yu61td3t.svg"
        },
        {
            id: "mobile-component-H-lozenge-partner-research-notes",
            x: 50,
            y: 40,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.partnerResearchNotes",
                defaultMessage: "Partner Research Notes"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-H-lozenge-fin-300",
            x: 55,
            y: 50,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.fin300AccountMicroservice",
                defaultMessage: "FIN-300: Account Microservice"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-H-lozenge-design-walkthrough",
            x: 65,
            y: 60,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.designWalkthrough",
                defaultMessage: "Design Walkthrough"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie"
        },
        {
            id: "mobile-component-H-rectangle-avatars",
            x: 65,
            y: 84,
            width: 700,
            height: 400,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/32agfh01wn5t07ah5781p287ap8sxoh4.png"
        },
        {
            id: "mobile-component-H-dot-bottom",
            x: 20,
            y: 100,
            size: 20,
            shape: "circle"
        }
    ];

    const teamworkGraphMobileNodesI: WebNodeConfig[] = [
        {
            id: "mobile-component-I-frame-text",
            x: 50,
            y: 25,
            shape: "rectangle",
            bgColor: "transparent",
            width: 700,
            height: 800,
            textColor: "white",
            fontFamily: "charlie",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={mobileHeadingCss}>{frame5HeadingWithDefault}</h3>
                    <p style={mobileDescriptionCss}>
                        {frame5DescriptionWithDefault}
                    </p>
                </div>
            )
        },
        {
            id: "mobile-component-I-dot-top",
            x: 20,
            y: 0,
            size: 20,
            shape: "circle"
        },
        {
            id: "mobile-component-I-dot-mid",
            x: 10,
            y: 40,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-I-dot-left-mid",
            x: -9,
            y: 60,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-I-dot-right-mid",
            x: 110,
            y: 60,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-I-dot-left-bottom",
            x: -9,
            y: 90,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-I-dot-right-bottom",
            x: 111,
            y: 90,
            size: 1,
            shape: "circle",
            bgColor: "transparent"
        },
        {
            id: "mobile-component-I-avatar-blue",
            x: 50,
            y: 80,
            shape: "circle",
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/vvs64iqhos5vy1u702ok4u50s42m10ii.png",
            size: 425
        }
    ];

    /* desktop nodes */

    const teamworkGraphAllFramesNodes: WebNodeConfig[] = [
        {
            id: "perpetual-grid",
            x: 150,
            y: 99,
            width: 880,
            height: 700,
            opacity: 0,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage: GRID_IMG,
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    opacity: 1,
                    x: 100,
                    y: 99
                },
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    opacity: 1,
                    x: 50,
                    y: 0
                },
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    opacity: 1,
                    x: 0,
                    y: 99
                },
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    opacity: 1,
                    x: 99,
                    y: 0
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-grid-center",
            x: 20,
            y: 42,
            width: 440,
            height: 350,
            opacity: 0,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage: GRID_IMG,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 50,
                    y: 50,
                    width: 880,
                    height: 701,
                    opacity: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-text-get-knowledge-flowing",
            x: 28,
            y: 18,
            shape: "rectangle",
            width: 500,
            height: 200,
            bgColor: "transparent",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    {frame1Heading &&
                    !isRichTextValueConsideredEmpty(frame1Heading) ? (
                        <UniformRichTextNode
                            node={
                                (frame1Heading["root"] ||
                                    frame1Heading) as RichTextNode
                            }
                        />
                    ) : (
                        <h2>
                            <FormattedMessage
                                id="gwp.refresh.teamworkGraph.frame1.heading"
                                values={DEFAULT_VALUES}
                                defaultMessage="Get knowledge <br></br> <strong>flowing</strong>"
                            />
                        </h2>
                    )}
                </div>
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: -50,
                    y: -100,
                    opacity: 0,
                    size: 180
                }
            ]
        },
        {
            id: "frame-1-text-starts-with-context",
            x: 28,
            y: 83,
            shape: "rectangle",
            width: 500,
            height: 300,
            bgColor: "transparent",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    {frame1Subheading &&
                    !isRichTextValueConsideredEmpty(frame1Subheading) ? (
                        <UniformRichTextNode
                            node={
                                (frame1Subheading["root"] ||
                                    frame1Subheading) as RichTextNode
                            }
                        />
                    ) : (
                        <>
                            <h3 style={desktopHeadingCss}>
                                {intl.formatMessage({
                                    id: "gwp.refresh.teamworkGraph.frame1.subheading",
                                    defaultMessage:
                                        "It all starts with context…"
                                })}
                            </h3>
                            <p style={desktopDescriptionCSS}>
                                {intl.formatMessage({
                                    id: "gwp.refresh.teamworkGraph.frame1.description",
                                    defaultMessage:
                                        "Link teams, work, and goals to unlock context right in your flow of work."
                                })}
                            </p>
                        </>
                    )}
                    <Link
                        bgColor="bgBlackBlue"
                        href={"/platform/teamwork-graph"}
                        aria-label={intl.formatMessage({
                            id: "gwp.refresh.teamworkGraph.frame1.link",
                            defaultMessage: "Explore Teamwork Graph"
                        })}
                        text={intl.formatMessage({
                            id: "gwp.refresh.teamworkGraph.frame1.link",
                            defaultMessage: "Explore Teamwork Graph"
                        })}
                        dataEventProps={{
                            "data-product-key": "uniform",
                            "data-event": "clicked",
                            "data-event-component": "link",
                            "data-event-container": "teamworkGraph",
                            "data-label": "Explore Teamwork Graph",
                            "data-label-english": "Explore Teamwork Graph"
                        }}
                    />
                </div>
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: -18,
                    y: 150,
                    opacity: 1,
                    size: 130
                }
            ]
        },
        {
            id: "all-frames-behind-avatar-blue",
            x: 20,
            y: 42,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "blue800",
            borderColor: "blue400",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 50,
                    y: 50,
                    opacity: 0.5,
                    size: 320
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_1_START,
                    scrollWindowEnd: FRAME_1_ADJUST_1_END,
                    x: 50,
                    y: 50,
                    opacity: 0.2,
                    size: 300
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT,
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    opacity: 0
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    opacity: 0,
                    x: 150,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 50,
                    y: 50,
                    size: 210
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_2_START,
                    scrollWindowEnd: FRAME_6_ADJUST_2_END,
                    x: 50,
                    y: 50,
                    size: 350,
                    opacity: 0.5
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_3_START,
                    scrollWindowEnd: FRAME_6_ADJUST_3_END,
                    x: 50,
                    y: 50,
                    size: 1,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-squiggle",
            x: 28,
            y: 31,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/xcosenf3w13o8pq585o705fxys5q03x2.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 50,
                    y: 50,
                    size: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },

        {
            id: "frame-1-avatar-blue",
            x: 20,
            y: 42,
            shape: "circle",
            size: AVATAR_SHRUNKEN_SIZE,
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/vvs64iqhos5vy1u702ok4u50s42m10ii.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 50,
                    y: 50,
                    opacity: 1,
                    size: AVATAR_NORMAL_SIZE
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-pill-project",
            x: 9,
            y: 33,
            shape: "pill",
            size: PILL_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4RZ.svg",
            imagePlacement: "insideLeading",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.project",
                defaultMessage: "Project"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 20,
                    y: 20,
                    opacity: 1,
                    size: PILL_NORMAL_SIZE
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-agent-top",
            x: 27,
            y: 40.5,
            shape: "circle",
            size: 1,
            bgColor: "transparent",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZG.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 74,
                    y: 44,
                    size: 30
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-agent-left",
            x: 22,
            y: 48,
            shape: "circle",
            size: 1,
            bgColor: "transparent",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZH.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 65,
                    y: 64,
                    size: 30
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-agent-right",
            x: 31,
            y: 46,
            shape: "circle",
            size: 1,
            bgColor: "transparent",
            opacity: 1,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZF.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 88,
                    y: 59,
                    size: 30
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-loom-to-shrink",
            x: 27,
            y: 48,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/4t8837ogw4s4t31c7x0hgfb523o7s4v1.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 76,
                    y: 65,
                    opacity: 1,
                    size: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },

        {
            id: "frame-1-shrink-to-microsoft-teams",
            x: 27,
            y: 48,
            shape: "rectangle",
            size: 1,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N54W.png",
            borderColor: "white",
            borderWidth: 2,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 76,
                    y: 65,
                    opacity: 1,
                    size: ICON_NORMAL_SIZE
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-icon-circle-arrow-with-sprint-text",
            x: 12,
            y: 47,
            shape: "rectangle",
            size: 1,
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.sprint",
                defaultMessage: "Sprint"
            }),
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZE.svg",
            imagePlacement: "insideLeading",
            borderColor: "blue600",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 25,
                    y: 50,
                    size: ICON_NORMAL_SIZE
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-icon-circle-arrow",
            x: 12,
            y: 47,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "blue600",
            opacity: 1,
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/6k3dp10ut224t4oa4ih2ns74gx2r07yh.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 25,
                    y: 50,
                    size: 1,
                    opacity: 0
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-icon-figma",
            x: 18,
            y: 53,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 20,
                    y: 75,
                    size: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-icon-people-team-with-team-text",
            x: 27,
            y: 61,
            shape: "rectangle",
            size: 1,
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.teamtext",
                defaultMessage: "Team"
            }),
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZC.svg",
            imagePlacement: "insideLeading",
            borderColor: "blue600",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 20,
                    y: 75,
                    opacity: 1,
                    size: ICON_NORMAL_SIZE
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-icon-people-team",
            x: 27,
            y: 61,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "blue600",
            opacity: 1,
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/301nl85gds5kr47oo2125d47333e6c73.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 20,
                    y: 75,
                    opacity: 0,
                    size: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },

        {
            id: "frame-1-icon-jira",
            x: 8,
            y: 60,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "blue600",
            bgImage: JIRA_IMG,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 10,
                    y: 50,
                    size: ICON_NORMAL_SIZE,
                    opacity: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-chip-team-r",
            x: 32,
            y: 40,
            shape: "rectangle",
            size: 1,
            opacity: 0,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZC.svg",
            imagePlacement: "insideLeading",
            borderColor: "blue500",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.team",
                defaultMessage: "Team"
            }),
            textColor: "white",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 70,
                    y: 25,
                    opacity: 0,
                    size: 1
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_1_START,
                    scrollWindowEnd: FRAME_1_ADJUST_1_END,
                    x: 74,
                    y: 15,
                    size: 1,
                    opacity: 1
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_2_START,
                    scrollWindowEnd: FRAME_1_ADJUST_2_END,
                    x: 74,
                    y: 15,
                    size: 46,
                    opacity: 1
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_3_START,
                    scrollWindowEnd: FRAME_1_ADJUST_3_END,
                    x: 95,
                    y: 22,
                    size: 46,
                    opacity: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-chip-video-tr",
            x: 32,
            y: 40,
            shape: "rectangle",
            size: 1,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZD.svg",
            imagePlacement: "insideLeading",
            borderColor: "blue500",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.video",
                defaultMessage: "Video"
            }),
            textColor: "white",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 70,
                    y: 25,
                    size: 46
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_1_START,
                    scrollWindowEnd: FRAME_1_ADJUST_1_END,
                    x: 74,
                    y: 15,
                    size: 46,
                    opacity: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-chip-video-left",
            x: -30,
            y: 50,
            shape: "rectangle",
            size: 1,
            opacity: 0,
            bgColor: "black",
            borderColor: "blue500",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.video",
                defaultMessage: "Video"
            }),
            textColor: "white",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 3,
                    y: 34,
                    opacity: 1,
                    size: 46
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-icon-behind-microsoft-teams",
            x: 32,
            y: 40,
            shape: "circle",
            size: 1,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(32, 40),
                    size: 1,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-microsoft-teams-to-shrink",
            x: 32,
            y: 40,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N54W.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 90,
                    y: 40,
                    opacity: 0,
                    size: 1
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-1-shrink-to-icon-loom",
            x: 32,
            y: 40,
            shape: "rectangle",
            size: 1,
            opacity: 0,
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/4t8837ogw4s4t31c7x0hgfb523o7s4v1.svg",

            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    x: 90,
                    y: 40,
                    opacity: 1,
                    size: ICON_NORMAL_SIZE
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },

        /* nodes that go away */
        {
            id: "frame-1-pill-work-item",
            x: 34,
            y: 54,
            shape: "pill",
            size: PILL_SHRUNKEN_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S2.svg",
            imagePlacement: "insideLeading",
            borderColor: "lime400",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.workItem",
                defaultMessage: "Work Item"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(34, 54),
                    size: PILL_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-avatar-green",
            x: 42,
            y: 35,
            shape: "circle",
            size: AVATAR_SHRUNKEN_SIZE,
            bgColor: "lime500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/ppop74d2k67771avyr8hs635pkasin70.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(42, 35),
                    size: AVATAR_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-rocket",
            x: 50,
            y: 33,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "lime500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/6ql83bjjch2xkvh7643ki04g3k2vkj1p.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(50, 33),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-brackets",
            x: 39,
            y: 45,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "lime500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/u33nvnm154b801i82t4075s0j0f2q727.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(39, 45),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-bitbucket",
            x: 45,
            y: 51,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "lime500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/s6xb2173woj4m00n36b754t44126al0l.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(45, 51),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },

        {
            id: "frame-1-pill-roadmap",
            x: 67,
            y: 15,
            shape: "pill",
            size: PILL_SHRUNKEN_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S2.svg",
            imagePlacement: "insideLeading",
            borderColor: "purple500",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.roadmap",
                defaultMessage: "Roadmap"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(67, 15),
                    size: PILL_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-avatar-purple",
            x: 75,
            y: 37,
            shape: "circle",
            size: AVATAR_SHRUNKEN_SIZE,
            bgColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/yk6fc6h475wmi67s827prloh073l01g1.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(75, 37),
                    size: AVATAR_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-lightbulb",
            x: 77,
            y: 23,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/1di7856lu37ve153ja4m4wcg17b8y26j.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(77, 23),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-video",
            x: 57,
            y: 25,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/0qdhoxv5o6b3mleiuc6ej43re1gsn8b0.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(57, 25),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-target",
            x: 70,
            y: 47,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/65wycilre16o225ia50e84d3a22ch140.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(70, 47),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-bookmark",
            x: 67,
            y: 32,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/2v746wlx1l1r41a6s5p63e7y102ko2l4.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(67, 32),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-jpd",
            x: 84,
            y: 30,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/klkr50gx181r0p8chweefx38e7hf48s1.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(84, 30),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-avatar-yellow",
            x: 63,
            y: 54,
            shape: "circle",
            size: AVATAR_SHRUNKEN_SIZE,
            bgColor: "yellow300",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/ua8r3l3j055g4k8t3vcw5x0qd3b3b3wa.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(63, 54),
                    size: AVATAR_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-lightning-bolt",
            x: 59,
            y: 41,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "yellow300",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/uo0v4gsxcfjo34e3evp630ba0653j558.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(59, 41),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-jsm",
            x: 57,
            y: 60,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "yellow300",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/nj3c8i7014gw1sba21en752f0q4pq0sx.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(57, 60),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-bell",
            x: 69,
            y: 65,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "yellow300",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/3gjch6tk0wbw3801u4656e82213h5xvm.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(69, 65),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },

        {
            id: "frame-1-pill-focus-area",
            x: 84,
            y: 70,
            shape: "pill",
            size: PILL_SHRUNKEN_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S7.svg",
            imagePlacement: "insideLeading",
            borderColor: "yellow500",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame1.focusArea",
                defaultMessage: "Focus Area"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(84, 70),
                    size: PILL_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-avatar-orange",
            x: 92,
            y: 47,
            shape: "circle",
            size: AVATAR_SHRUNKEN_SIZE,
            bgColor: "yellow500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/5o2o2enoi03yotn523u7t1505kw351en.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(92, 47),
                    size: AVATAR_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-dollar",
            x: 81,
            y: 43,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "yellow500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/qmtxbl7050fm60rs86110t4h8553436v.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(81, 43),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-star-talent",
            x: 88,
            y: 59,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "yellow500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/fwhk18bh22311008ugi5s2i62s5iv632.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(88, 59),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-csm",
            x: 52,
            y: 50,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "yellow300",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N54R.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(52, 50),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-microsoft",
            x: 93,
            y: 31,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/72jyk7b1r48ul3cb6qusr6b1yu61td3t.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(93, 31),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-gdrive",
            x: 62,
            y: 71,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/x6fti424cg1dyn52m750hns8386vyw2o.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(62, 71),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },
        {
            id: "frame-1-icon-sharepoint",
            x: 75,
            y: 57,
            shape: "rectangle",
            size: ICON_SHRUNKEN_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N539.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ZOOMIN_START,
                    scrollWindowEnd: FRAME_1_ZOOMIN_END,
                    ...frame1NodeLeavingZoomEndPosition(75, 57),
                    size: ICON_NORMAL_SIZE,
                    opacity: 0
                }
            ]
        },

        /* frame 2 bridging elements */
        {
            id: "frame-1-and-2-bridge",
            x: 101,
            y: 80,
            shape: "circle",
            size: 5,
            bgColor: "blue300",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_1_START,
                    scrollWindowEnd: FRAME_1_ADJUST_1_END,
                    x: 50,
                    y: 80
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_2_START,
                    scrollWindowEnd: FRAME_1_ADJUST_2_END,
                    x: 120,
                    y: 30
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 20,
                    y: 30
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-1-prompt-box",
            x: 101,
            y: 80,
            shape: "rectangle",
            size: 1,
            width: 1,
            height: 1,
            opacity: 1,
            borderColor: "rainbowRovo",
            bgColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MT.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_1_ADJUST_1_START,
                    scrollWindowEnd: FRAME_1_ADJUST_1_END,
                    x: 50,
                    y: 80,
                    opacity: 1,
                    width: 350,
                    height: 115
                },
                ZOOMED_IN_NODES_SHIFTING_LEFT
            ]
        },

        /* frame 2 */

        {
            id: "frame-2-text",
            x: textPosition.x + 100,
            y: textPosition.y,
            width: 300,
            height: 400,
            opacity: 0,
            shape: "rectangle",
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "transparent",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={desktopHeadingCss}>
                        {frame2HeadingWithDefault}
                    </h3>
                    <p style={desktopDescriptionCSS}>
                        {frame2DescriptionWithDefault}
                    </p>
                </div>
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    opacity: 1,
                    x: textPosition.x,
                    y: textPosition.y
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-icon-confluence",
            x: 120,
            y: 30,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            opacity: 1,
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MY.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 20,
                    y: 30
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-icon-hubspot",
            x: 140,
            y: 15,
            shape: "rectangle",
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MV.svg",
            size: ICON_NORMAL_SIZE,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 40,
                    y: 15
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-icon-figma",
            x: 175,
            y: 30,
            shape: "rectangle",
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg",
            size: ICON_NORMAL_SIZE,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 75,
                    y: 30
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-dot-top-right",
            x: 225,
            y: -10,
            shape: "circle",
            size: 1,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 125,
                    y: -10
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-dot-top",
            x: 125,
            y: -50,
            shape: "circle",
            size: 1,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 25,
                    y: -50
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-rovo-prompt-text",
            x: videoPosition.x + 100,
            y: videoPosition.y,
            shape: "rectangle",
            size: 70,
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "neutral900",
            borderColor: "rainbowRovo",
            borderRadius: 5,
            text: pullingInContextString,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S0.svg",
            imagePlacement: "insideLeading",
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-lozenge-1",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FX.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame2.lozenge1",
                defaultMessage: "Marketing / Veronica"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_2_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_2_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[0].x,
                    y: LOZENGE_POSITIONS[0].y,
                    opacity: 1
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-lozenge-2",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FS.png",
            text: intl.formatMessage({
                id: "teamworkGraph.frame2.lozenge1",
                defaultMessage: "Meeting Recordings"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_2_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_2_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[1].x,
                    y: LOZENGE_POSITIONS[1].y,
                    opacity: 1
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-lozenge-3",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FT.png",
            text: intl.formatMessage({
                id: "teamworkGraph.frame2.lozenge1",
                defaultMessage: "Campaign Ad Refresh"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_2_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_2_PULLING_CONTEXT_STEP_3,
                    x: LOZENGE_POSITIONS[2].x,
                    y: LOZENGE_POSITIONS[2].y,
                    opacity: 1
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },
        {
            id: "frame-2-video-expand",
            x: videoPosition.x + 100,
            y: videoPosition.y,
            shape: "rectangle",
            width: 10,
            height: 10,
            opacity: 0,
            bgColor: "neutral800",
            borderColor: "rainbowRovo",
            borderRadius: 10,
            bgImage: bgImageSafari(
                frame2VideoImg ||
                    "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N40J.webp"
            ),
            htmlContent: htmlVideoSafari(
                frame2VideoUrl ||
                    "https://dam-cdn.atl.orangelogic.com/AssetLink/elqg4r36c8cgw17o0yh87a6ghe6s5e75.webm"
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: videoPosition.x,
                    y: videoPosition.y,
                    opacity: 0
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_2_VIDEO_EXPAND_START,
                    scrollWindowEnd: FRAME_2_VIDEO_EXPAND_END,
                    width: vidFrontDimension,
                    height: vidFrontDimension,
                    opacity: 1
                },
                FRAME_2_NODES_SHIFTING_UP_LEFT
            ]
        },

        /* frame 3 */

        {
            id: "frame-3-text",
            x: textPosition.x + 100,
            y: textPosition.y + 100,
            width: 300,
            height: 400,
            opacity: 0,
            shape: "rectangle",
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "transparent",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={desktopHeadingCss}>
                        {frame3HeadingWithDefault}
                    </h3>
                    <p style={desktopDescriptionCSS}>
                        {frame3DescriptionWithDefault}
                    </p>
                </div>
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    opacity: 1,
                    x: textPosition.x,
                    y: textPosition.y
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-icon-gdrive",
            x: 220,
            y: 130,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/x6fti424cg1dyn52m750hns8386vyw2o.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 120,
                    y: 130
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 20,
                    y: 30
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-icon-cursor",
            x: 250,
            y: 170,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MZ.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 150,
                    y: 170
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 50,
                    y: 70
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-dot-bottom",
            x: 230,
            y: 210,
            shape: "rectangle",
            size: 1,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: 130,
                    y: 210
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 30,
                    y: 110
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "behind-frame-3-and-5-dot-1",
            x: 183,
            y: 130,
            shape: "rectangle",
            size: 5,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 83,
                    y: 30
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT,
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: 65,
                    y: 20
                },
                FRAME_5_NODES_SHIFTING_LEFT,
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 0
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "behind-frame-3-and-5-dot-2",
            x: 183,
            y: 130,
            shape: "rectangle",
            size: 5,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 83,
                    y: 30
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT,
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: 65,
                    y: 20
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1]
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-3-and-5-icon-jira",
            x: 183,
            y: 130,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MN9C.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 83,
                    y: 30
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT,
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: 65,
                    y: 20
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-3-dot-left",
            x: 0,
            y: 200,
            shape: "circle",
            size: 15,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: -50,
                    y: 100
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },

        {
            id: "frame-3-and-5-dot-right-bottom",
            x: 250,
            y: 200,
            shape: "circle",
            size: 1,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 150,
                    y: 100
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT,
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: 0,
                    y: 0
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-3-rovo-prompt-text",
            x: videoPosition.x + 100,
            y: videoPosition.y + 100,
            shape: "rectangle",
            size: 70,
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "neutral900",
            borderColor: "rainbowRovo",
            borderRadius: 5,
            text: pullingInContextString,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S0.svg",
            imagePlacement: "insideLeading",
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-lozenge-1",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y + 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4G0.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame3.lozenge1",
                defaultMessage: "Strategy Brief"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_3_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_3_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[0].x,
                    y: LOZENGE_POSITIONS[0].y,
                    opacity: 1
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-lozenge-2",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y + 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4G1.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame3.lozenge1",
                defaultMessage: "Portal Repo 2.0"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_3_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_3_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[1].x,
                    y: LOZENGE_POSITIONS[1].y,
                    opacity: 1
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-lozenge-3",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y + 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FZ.png",
            text: intl.formatMessage({
                id: "teamworkGraph.frame3.lozenge1",
                defaultMessage: "Portal Feature Gates"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_3_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_3_PULLING_CONTEXT_STEP_3,
                    x: LOZENGE_POSITIONS[2].x,
                    y: LOZENGE_POSITIONS[2].y,
                    opacity: 1
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        {
            id: "frame-3-video-expand",
            x: videoPosition.x + 100,
            y: videoPosition.y + 100,
            shape: "rectangle",
            width: 10,
            height: 10,
            opacity: 0,
            bgColor: "neutral800",
            borderColor: "rainbowRovo",
            borderRadius: 10,
            bgImage: bgImageSafari(
                frame3VideoImg ||
                    "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N40H.webp"
            ),
            htmlContent: htmlVideoSafari(
                frame3VideoUrl ||
                    "https://dam-cdn.atl.orangelogic.com/AssetLink/30e047lljh5y7j5n433ls66055u8t018.webm"
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: videoPosition.x,
                    y: videoPosition.y,
                    width: 1,
                    height: 1,
                    opacity: 0
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_3_VIDEO_EXPAND_START,
                    scrollWindowEnd: FRAME_3_VIDEO_EXPAND_END,
                    width: vidFrontDimension,
                    height: vidFrontDimension,
                    opacity: 1
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },

        /* bridge from frame 3 to frame 4 */

        {
            id: "frame-3-and-4-bridge",
            x: 222,
            y: 120,
            shape: "circle",
            size: 1,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_2_TO_3_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_2_TO_3_END,
                    x: 122,
                    y: 20
                },
                FRAME_3_NODES_SHIFTING_DOWN_LEFT
            ]
        },
        /* frame 4 */
        {
            id: "frame-4-icon-loom",
            x: 120,
            y: -80,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MN9D.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: 20,
                    y: 20
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-icon-sharepoint",
            x: 170,
            y: -70,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N539.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: 70,
                    y: 30
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-icon-canva",
            x: 150,
            y: -90,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5Q6.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: 50,
                    y: 10
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-dot-top",
            x: 175,
            y: -90,
            shape: "circle",
            size: 4,
            opacity: 1,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: 75,
                    y: -10
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-dot-right",
            x: 205,
            y: -20,
            shape: "circle",
            size: 4,
            opacity: 1,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: 105,
                    y: 80
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-text",
            x: textPosition.x + 100,
            y: textPosition.y - 100,
            width: 300,
            height: 400,
            opacity: 0,
            shape: "rectangle",
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "transparent",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={desktopHeadingCss}>
                        {frame4HeadingWithDefault}
                    </h3>
                    <p style={desktopDescriptionCSS}>
                        {frame4DescriptionWithDefault}
                    </p>
                </div>
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    opacity: 1,
                    x: textPosition.x,
                    y: textPosition.y
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-rovo-prompt-text",
            x: videoPosition.x + 100,
            y: videoPosition.y - 100,
            shape: "rectangle",
            size: 70,
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "neutral900",
            borderColor: "rainbowRovo",
            borderRadius: 5,
            text: pullingInContextString,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S0.svg",
            imagePlacement: "insideLeading",
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-lozenge-1",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y - 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z2.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame4.lozenge1",
                defaultMessage: "Portal Redesign Brainstorm"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_4_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_4_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[0].x,
                    y: LOZENGE_POSITIONS[0].y,
                    opacity: 1
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-lozenge-2",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y - 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z3.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame4.lozenge1",
                defaultMessage: "Portal Designs"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_4_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_4_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[1].x,
                    y: LOZENGE_POSITIONS[1].y,
                    opacity: 1
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-lozenge-3",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x + 100,
            y: videoPosition.y - 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z4.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame4.lozenge1",
                defaultMessage: " [O1.KR2] Add 300K Accounts"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_1_TO_2_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_1_TO_2_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_4_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_4_PULLING_CONTEXT_STEP_3,
                    x: LOZENGE_POSITIONS[2].x,
                    y: LOZENGE_POSITIONS[2].y,
                    opacity: 1
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },
        {
            id: "frame-4-video-expand",
            x: videoPosition.x + 100,
            y: videoPosition.y - 100,
            shape: "rectangle",
            width: 10,
            height: 10,
            opacity: 0,
            bgColor: "neutral800",
            borderColor: "rainbowRovo",
            borderRadius: 10,
            bgImage: bgImageSafari(
                frame4VideoImg ||
                    "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N40I.webp"
            ),
            htmlContent: htmlVideoSafari(
                frame4VideoUrl ||
                    "https://dam-cdn.atl.orangelogic.com/AssetLink/ny32egn16elq2656p0mpl83368pr3wlx.webm"
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_3_TO_4_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_3_TO_4_END,
                    x: videoPosition.x,
                    y: videoPosition.y,
                    width: 1,
                    height: 1,
                    opacity: 0
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_4_VIDEO_EXPAND_START,
                    scrollWindowEnd: FRAME_4_VIDEO_EXPAND_END,
                    width: vidFrontDimension,
                    height: vidFrontDimension,
                    opacity: 1
                },
                FRAME_4_NODES_SHIFTING_UP_RIGHT
            ]
        },

        /* frame 5 */

        {
            id: "frame-5-text",
            x: textPosition.x - 100,
            y: textPosition.y + 100,
            width: 300,
            height: 400,
            opacity: 0,
            shape: "rectangle",
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "transparent",
            htmlContent: (
                <div style={{ textAlign: "left" }}>
                    <h3 style={desktopHeadingCss}>
                        {frame5HeadingWithDefault}
                    </h3>
                    <p style={desktopDescriptionCSS}>
                        {frame5DescriptionWithDefault}
                    </p>
                </div>
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    opacity: 1,
                    x: textPosition.x,
                    y: textPosition.y
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-5-rovo-prompt-text",
            x: videoPosition.x - 100,
            y: videoPosition.y + 100,
            shape: "rectangle",
            size: 70,
            fontFamily: "charlie",
            fontWeight: 400,
            bgColor: "neutral900",
            borderColor: "rainbowRovo",
            borderRadius: 5,
            text: pullingInContextString,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4S0.svg",
            imagePlacement: "insideLeading",
            animationPhases: [
                {
                    delay: 0,
                    duration: 1,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-5-lozenge-1",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x - 100,
            y: videoPosition.y + 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4YZ.png",
            text: intl.formatMessage({
                id: "teamworkGraph.frame5.lozenge1",
                defaultMessage: "Partner Calls"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_5_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_5_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[0].x,
                    y: LOZENGE_POSITIONS[0].y,
                    opacity: 1
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-5-lozenge-2",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x - 100,
            y: videoPosition.y + 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z0.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame5.lozenge1",
                defaultMessage: "FIN-300: Account Microservice"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_5_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_5_PULLING_CONTEXT_STEP_2,
                    x: LOZENGE_POSITIONS[1].x,
                    y: LOZENGE_POSITIONS[1].y,
                    opacity: 1
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-5-lozenge-3",
            ...LOZENGE_ATTRIBUTES,
            x: videoPosition.x - 100,
            y: videoPosition.y + 100,
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z1.svg",
            text: intl.formatMessage({
                id: "teamworkGraph.frame5.lozenge1",
                defaultMessage: "Design Walkthrough"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: videoPosition.x,
                    y: videoPosition.y
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_5_PULLING_CONTEXT_STEP_1,
                    scrollWindowEnd: FRAME_5_PULLING_CONTEXT_STEP_3,
                    x: LOZENGE_POSITIONS[2].x,
                    y: LOZENGE_POSITIONS[2].y,
                    opacity: 1
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-5-icon-microsoft",
            shape: "rectangle",
            x: -60,
            y: 177,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MNA0.svg",
            size: ICON_NORMAL_SIZE,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: 40,
                    y: 77
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        {
            id: "frame-5-video-expand",
            x: videoPosition.x - 100,
            y: videoPosition.y + 100,
            shape: "rectangle",
            width: 10,
            height: 10,
            opacity: 0,
            bgColor: "neutral800",
            borderColor: "rainbowRovo",
            borderRadius: 10,
            bgImage: bgImageSafari(
                frame5VideoImg ||
                    "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N40G.webp"
            ),
            htmlContent: htmlVideoSafari(
                frame5VideoUrl ||
                    "https://dam-cdn.atl.orangelogic.com/AssetLink/jl7534n20m4g3xtquu6wn3i5i13113q5.webm"
            ),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_4_TO_5_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_4_TO_5_END,
                    x: videoPosition.x,
                    y: videoPosition.y,
                    width: 1,
                    height: 1,
                    opacity: 0
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_5_VIDEO_EXPAND_START,
                    scrollWindowEnd: FRAME_5_VIDEO_EXPAND_END,
                    width: vidFrontDimension,
                    height: vidFrontDimension,
                    opacity: 1
                },
                FRAME_5_NODES_SHIFTING_LEFT
            ]
        },
        /* frame 6 */

        {
            id: "frame-6-grid-left",
            x: 150,
            y: 30,
            width: 880,
            height: 701,
            opacity: 0,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage: GRID_IMG,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 0,
                    opacity: 1
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-grid-right",
            x: 200,
            y: 90,
            width: 880,
            height: 701,
            opacity: 0,
            shape: "rectangle",
            bgColor: "transparent",
            bgImage: GRID_IMG,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 100,
                    opacity: 1
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-behind-assets-top",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1]
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-behind-assets-bottom",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1]
                },

                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 7.4,
                    y: 70
                }
            ]
        },
        {
            id: "frame-6-behind-in-progress-top",
            x: 180,
            y: 30,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 80,
                    y: 30
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.6,
                    y: 74.9
                }
            ]
        },
        {
            id: "frame-6-behind-in-progress-bottom",
            x: 180,
            y: 30,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 80,
                    y: 30
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 30.7,
                    y: 63.8
                }
            ]
        },
        {
            id: "frame-6-behind-done-top",
            x: 195,
            y: 80,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 95,
                    y: 80
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 33.1,
                    y: 82.6
                }
            ]
        },
        {
            id: "frame-6-behind-done-bottom",
            x: 195,
            y: 80,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 95,
                    y: 80
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-marketing-team",
            x: 110,
            y: 20,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.marketingTeam",
                defaultMessage: "Marketing team"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FX.svg",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            size: 20,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 10,
                    y: 25,
                    opacity: 0,
                    size: 20
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 10,
                    y: 30,
                    opacity: 1,
                    size: 30
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-meeting-recordings",
            x: 113,
            y: 20,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.meetingRecordings",
                defaultMessage: "Meeting Recordings"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FS.png",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            size: 20,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 10,
                    y: 25,
                    opacity: 0,
                    size: 20
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_2_END,
                    x: 13,
                    y: 35,
                    opacity: 1,
                    size: 30
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-campaign-ad-refresh",
            x: 116,
            y: 20,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.campaignAdRefresh",
                defaultMessage: "Campaign Ad Refresh"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MU.png",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            size: 20,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 10,
                    y: 25,
                    opacity: 0,
                    size: 20
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_3_END,
                    x: 16,
                    y: 40,
                    opacity: 1,
                    size: 30
                },
                FRAME_6_NODES_SHRINKING
            ]
        },

        {
            id: "frame-6-rectangle-assets",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "rectangle",
            width: 100,
            height: 50,
            opacity: 0,
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/n77suu8uq6g15lwd755l77a3833ai6b3.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1],
                    width: 200,
                    height: 100,
                    opacity: 1
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "final-screen-behind-squiggle-to-idea",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "circle",
            opacity: 0,
            size: 1,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1],
                    opacity: 0,
                    size: 1
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 1.6,
                    y: 49,
                    opacity: 1
                }
            ]
        },
        {
            id: "final-screen-icon-squiggle",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "rectangle",
            size: 1,
            opacity: 0,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/xcosenf3w13o8pq585o705fxys5q03x2.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1],
                    opacity: 0,
                    size: 1
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 7.4,
                    y: 70,
                    size: ICON_SHRUNKEN_SIZE,
                    opacity: 1
                }
            ]
        },
        {
            id: "frame-6-dot-offscreen-bottom-left",
            x: 100,
            y: 100,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: -30,
                    y: 100
                }
            ]
        },
        {
            id: "frame-6-icon-figma",
            x: 150,
            y: 97,
            shape: "rectangle",
            borderColor: "white",
            bgColor: "black",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pjx7hy131bqolmfog4go5ej85dxwm7k7.svg",
            size: ICON_NORMAL_SIZE,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 50,
                    y: 97
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-behind-comment",
            x: 120,
            y: 75,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 20,
                    y: 75
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 16.4,
                    y: 91.3
                }
            ]
        },
        {
            id: "frame-6-rectangle-comment",
            x: 120,
            y: 75,
            shape: "rectangle",
            width: 100,
            height: 50,
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/2gf42uyw6x35g346v1337b15dx434t0a.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 20,
                    y: 75,
                    width: 200,
                    height: 100
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-behind-from-assets-to-avatars-rectangle",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1]
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 40,
                    y: 5
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-rectangle-avatars",
            x: 140,
            y: 5,
            shape: "rectangle",
            width: 200,
            height: 100,
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/32agfh01wn5t07ah5781p287ap8sxoh4.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 40,
                    y: 5
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-rectangle-in-progress",
            x: 180,
            y: 30,
            shape: "rectangle",
            width: 200,
            height: 100,
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/426lxf8r1u73b7e5768ts0dd2w26n11l.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 80,
                    y: 30
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-icon-loom",
            x: 80,
            y: 30,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            opacity: 0,
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/4t8837ogw4s4t31c7x0hgfb523o7s4v1.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 30.7,
                    y: 63.8,
                    size: ICON_SHRUNKEN_SIZE,
                    opacity: 1
                }
            ]
        },
        {
            id: "frame-6-rectangle-done",
            x: 195,
            y: 80,
            shape: "rectangle",
            width: 200,
            height: 100,
            bgColor: "transparent",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/b4d1tn2sdy0s36i56c857db6w1k1u2b4.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 95,
                    y: 80
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-icon-confluence",
            x: 95,
            y: 80,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            opacity: 0,
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MY.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 33.1,
                    y: 82.6,
                    size: ICON_SHRUNKEN_SIZE,
                    opacity: 1
                }
            ]
        },

        {
            id: "frame-6-behind-from-in-progress-to-avatar",
            x: 180,
            y: 30,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 80,
                    y: 30
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 50,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.6,
                    y: 74.9
                }
            ]
        },
        {
            id: "frame-6-behind-from-done-to-avatar",
            x: 195,
            y: 80,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 95,
                    y: 80
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 50,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.6,
                    y: 74.9
                }
            ]
        },
        {
            id: "frame-6-behind-from-assets-to-avatar",
            ...RECTANGLE_ASSETS_POSITIONS[0],
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    ...RECTANGLE_ASSETS_POSITIONS[1]
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 50,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.6,
                    y: 74.9
                }
            ]
        },

        {
            id: "frame-6-behind-from-comment-to-avatar",
            x: 120,
            y: 75,
            shape: "circle",
            size: 4,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 20,
                    y: 75
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_6_ADJUST_1_START,
                    scrollWindowEnd: FRAME_6_ADJUST_1_END,
                    x: 50,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.6,
                    y: 74.9
                }
            ]
        },
        {
            id: "final-screen-behind-avatar-blue-to-jpd",
            x: 150,
            y: 50,
            shape: "circle",
            size: 1,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 50,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 16.6,
                    y: 44.2
                }
            ]
        },
        {
            id: "final-screen-avatar-blue",
            x: 150,
            y: 50,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "blue600",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/vvs64iqhos5vy1u702ok4u50s42m10ii.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 50,
                    y: 50
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.6,
                    y: 74.9,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-6-lozenge-partner-research",
            x: 140,
            y: 15,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.partnerCalls",
                defaultMessage: "Partner Calls"
            }),
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4YZ.png",
            imagePlacement: "outsideLeading",
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 40
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-fin-300",
            x: 143,
            y: 20,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.fin300AccountMicroservice",
                defaultMessage: "FIN-300: Account Microservice"
            }),
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z0.svg",
            imagePlacement: "outsideLeading",
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 43
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-design-demo",
            x: 143,
            y: 25,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.designDemo",
                defaultMessage: "Design Demo"
            }),
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z1.svg",
            imagePlacement: "outsideLeading",
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 43
                },
                FRAME_6_NODES_SHRINKING
            ]
        },

        {
            id: "frame-6-lozenge-portal-redesign-brainstorm",
            x: 123,
            y: 85,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalRedesignBrainstorm",
                defaultMessage: "Portal Redesign Brainstorm"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z2.svg",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 23
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-portal-designs",
            x: 123,
            y: 90,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalDesigns",
                defaultMessage: "Portal Designs"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z3.svg",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 23
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-okr",
            x: 126,
            y: 95,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.okrAdd300kAccounts",
                defaultMessage: "[01.KR2] Add 300k accounts"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z4.svg",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 26
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-strategy-brief",
            x: 180,
            y: 40,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.strategyBrief",
                defaultMessage: "Strategy Brief"
            }),
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4Z2.svg",
            imagePlacement: "outsideLeading",
            shape: "rectangle",
            bgColor: "blue600",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 80
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-lozenge-portal-feature-gates",
            x: 183,
            y: 45,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalFeatureGates",
                defaultMessage: "Portal Feature Gates"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4FZ.png",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 83
                },
                FRAME_6_NODES_SHRINKING
            ]
        },
        {
            id: "frame-6-portal-repo-2-point-0",
            x: 186,
            y: 50,
            size: 30,
            text: intl.formatMessage({
                id: "teamworkGraph.frame6.portalRepo2",
                defaultMessage: "Portal Repo 2.0"
            }),
            shape: "rectangle",
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4G1.svg",
            imagePlacement: "outsideLeading",
            textColor: "white",
            fontFamily: "charlie",
            fontWeight: 400,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 86
                },
                FRAME_6_NODES_SHRINKING
            ]
        },

        /* frame 7 */

        {
            id: "frame-7-center-text",
            x: 140,
            y: 45,
            shape: "rectangle",
            width: 700,
            height: 200,
            opacity: 0,
            bgColor: "transparent",
            htmlContent: (
                <div style={{ textAlign: "center" }}>
                    {frame7Heading &&
                    !isRichTextValueConsideredEmpty(frame7Heading) ? (
                        <UniformRichTextNode
                            node={
                                (frame7Heading["root"] ||
                                    frame7Heading) as RichTextNode
                            }
                        />
                    ) : (
                        <h2>
                            <FormattedMessage
                                id="gwp.refresh.teamworkGraph.frame7.heading"
                                values={DEFAULT_VALUES}
                                defaultMessage="So you can make <br></br> <strong>smarter moves, faster</strong>"
                            />
                        </h2>
                    )}
                </div>
            ),
            textColor: "white",
            fontWeight: 400,
            fontFamily: "charlie",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 40,
                    y: 45,
                    opacity: 0
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_1_START,
                    scrollWindowEnd: FRAME_7_ADJUST_1_END,
                    x: 50,
                    y: 45,
                    opacity: 1
                }
            ]
        },
        {
            id: "frame-7-pill-meeting-notes",
            x: 20,
            y: 75,
            shape: "rectangle",
            size: 20,
            opacity: 0,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N50K.svg",
            imagePlacement: "insideLeading",
            borderColor: "blue600",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame7.meetingNotes",
                defaultMessage: "Meeting notes"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 16.4,
                    y: 91.3,
                    size: 47,
                    opacity: 1
                }
            ]
        },

        /* frame 7 — enter from off-screen during 6 to 7 transition */
        {
            id: "frame-7-icon-hubspot",
            x: 0,
            y: -100,
            shape: "rectangle",
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MV.svg",
            size: ICON_NORMAL_SIZE,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 5,
                    y: 5,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-dot-x-30-y-0",
            x: 90,
            y: -100,
            shape: "circle",
            size: 1,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 30,
                    y: -4
                }
            ]
        },
        {
            id: "frame-7-dot-x-50-y-0",
            x: 150,
            y: -100,
            shape: "circle",
            size: 1,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 50,
                    y: -4
                }
            ]
        },
        {
            id: "frame-7-dot-x-60-y-0",
            x: 200,
            y: -100,
            shape: "circle",
            size: 1,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 60,
                    y: -4
                }
            ]
        },
        {
            id: "frame-7-dot-x-65-y-0",
            x: 250,
            y: -100,
            shape: "circle",
            size: 1,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 65,
                    y: 0
                }
            ]
        },
        {
            id: "frame-7-dot-x-70-y-0",
            x: 260,
            y: -100,
            shape: "circle",
            size: 1,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 70,
                    y: -4
                }
            ]
        },
        {
            id: "frame-7-dot-x-80-y-0",
            x: 300,
            y: -100,
            shape: "circle",
            size: 1,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 80,
                    y: -4
                }
            ]
        },
        {
            id: "frame-7-icon-jira-top",
            x: 100,
            y: -99,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "blue600",
            bgImage: JIRA_IMG,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 38.4,
                    y: 1,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-focus-top",
            x: 200,
            y: -57,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "orange300",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MW.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 63.6,
                    y: 3.8,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-behind-avatar-lime-top",
            x: 250,
            y: -71,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "lime800",
            borderColor: "lime500",
            borderWidth: 8,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 81.8,
                    y: 8.8
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_2_START,
                    scrollWindowEnd: FRAME_7_ADJUST_2_END,
                    opacity: 0.2,
                    size: 150
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_3_START,
                    scrollWindowEnd: FRAME_7_ADJUST_3_END,
                    opacity: 0.05,
                    size: 1
                }
            ]
        },
        {
            id: "frame-7-avatar-lime-top",
            x: 250,
            y: -71,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "lime500",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N520.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 81.8,
                    y: 8.8,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-pill-campaign",
            x: 90,
            y: -99,
            shape: "rectangle",
            size: PILL_NORMAL_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N50N.svg",
            imagePlacement: "insideLeading",
            borderColor: "blue600",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame7.campaign",
                defaultMessage: "Campaign"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 18.2,
                    y: 8.8,
                    size: PILL_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-pill-pr",
            x: 300,
            y: -100,
            shape: "rectangle",
            size: PILL_NORMAL_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N4ZC.svg",
            imagePlacement: "insideLeading",
            borderColor: "lime500",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame7.pullRequestAbbrev",
                defaultMessage: "PR",
                description:
                    "Abbreviation for pull request, shown on a small pill label in a marketing animation"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 98.1,
                    y: 10,
                    size: PILL_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-behind-avatar-orange",
            x: 200,
            y: -60,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "orange800",
            borderColor: "orange300",
            borderWidth: 8,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 53,
                    y: 17.5
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_1_START,
                    scrollWindowEnd: FRAME_7_ADJUST_1_END,
                    opacity: 0.2,
                    size: 150
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_3_START,
                    scrollWindowEnd: FRAME_7_ADJUST_3_END,
                    opacity: 0.05,
                    size: 1
                }
            ]
        },
        {
            id: "frame-7-avatar-orange",
            x: 200,
            y: -60,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "orange300",
            textColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N51Z.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 53,
                    y: 17.5,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-bookmark",
            x: 90,
            y: -70,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/2v746wlx1l1r41a6s5p63e7y102ko2l4.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 27.7,
                    y: 17.7,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-bitbucket",
            x: 300,
            y: -20,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "lime500",
            textColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/s6xb2173woj4m00n36b754t44126al0l.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 92.3,
                    y: 22.9,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-rovo-dev",
            x: 250,
            y: -20,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "lime400",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N5MX.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 71.6,
                    y: 24.9,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-behind-avatar-purple",
            x: 50,
            y: -96,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "purple800",
            borderColor: "purple500",
            borderWidth: 8,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 8,
                    y: 27.6
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_2_START,
                    scrollWindowEnd: FRAME_7_ADJUST_2_END,
                    opacity: 0.2,
                    size: 150
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_3_START,
                    scrollWindowEnd: FRAME_7_ADJUST_3_END,
                    opacity: 0.05,
                    size: 1
                }
            ]
        },
        {
            id: "frame-7-avatar-purple",
            x: 50,
            y: -96,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "purple500",
            textColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N51X.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 8,
                    y: 27.6,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-pill-goal",
            x: 225,
            y: -10,
            shape: "rectangle",
            size: PILL_NORMAL_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N50M.svg",
            imagePlacement: "insideLeading",
            borderColor: "orange300",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame7.goal",
                defaultMessage: "Goal"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 61.4,
                    y: 31.1,
                    size: PILL_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-purple-jpd",
            x: 70,
            y: -20,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "purple500",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/67p4f030jo35j028ayr2hw4tioa5i200.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 16.6,
                    y: 44.2,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-pill-idea",
            x: 20,
            y: -20,
            shape: "rectangle",
            size: PILL_NORMAL_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N50L.svg",
            imagePlacement: "insideLeading",
            borderColor: "purple500",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame7.idea",
                defaultMessage: "Idea"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 1.6,
                    y: 49,
                    size: PILL_SHRUNKEN_SIZE
                }
            ]
        },

        /* frame 7 nodes moving downward */
        {
            id: "frame-7-behind-avatar-saffron",
            x: 280,
            y: 22,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "blue600",
            borderColor: "blue400",
            borderWidth: 8,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 86.4,
                    y: 52
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_1_START,
                    scrollWindowEnd: FRAME_7_ADJUST_1_END,
                    opacity: 0.2,
                    size: 150
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_3_START,
                    scrollWindowEnd: FRAME_7_ADJUST_3_END,
                    opacity: 0.05,
                    size: 1
                }
            ]
        },
        {
            id: "frame-7-avatar-saffron",
            x: 280,
            y: 22,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "blue600",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N521.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 86.4,
                    y: 52,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-rocket",
            x: 200,
            y: 20,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/ptn36e828x7k02745o673wfoe4mpofj2.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 57.1,
                    y: 62,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-target",
            x: 300,
            y: 63.5,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/65wycilre16o225ia50e84d3a22ch140.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 96.1,
                    y: 63.5,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-behind-loom",
            x: 180,
            y: 30,
            shape: "rectangle",
            size: 1,
            opacity: 0,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_5_TO_6_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_5_TO_6_END,
                    x: 80,
                    y: 30
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 30.7,
                    y: 63.8
                }
            ]
        },
        {
            id: "frame-7-icon-bell",
            x: 250,
            y: 25,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/3gjch6tk0wbw3801u4656e82213h5xvm.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 71.2,
                    y: 64.3,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-csm",
            x: 150,
            y: 40,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "yellow300",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N54R.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 46.1,
                    y: 73.7,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-gdrive",
            x: 270,
            y: 78.1,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "black",
            borderColor: "white",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MNA5.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 87.9,
                    y: 78.1,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-icon-behind-confluence",
            x: 100,
            y: 80,
            shape: "circle",
            size: 1,
            bgColor: "transparent",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 33.1,
                    y: 82.6
                }
            ]
        },
        {
            id: "frame-7-icon-jsm",
            x: 200,
            y: 40,
            shape: "rectangle",
            size: ICON_NORMAL_SIZE,
            bgColor: "yellow300",
            bgImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/nj3c8i7014gw1sba21en752f0q4pq0sx.svg",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 58.8,
                    y: 87.1,
                    size: ICON_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-dot-x-0-y-90",
            x: 100,
            y: 90,
            shape: "circle",
            size: 1,
            opacity: 0,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 0,
                    y: 90
                }
            ]
        },
        {
            id: "frame-7-behind-avatar-lime-bottom",
            x: 250,
            y: 91.8,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "lime800",
            borderColor: "lime500",
            borderWidth: 8,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 76.5,
                    y: 91.8
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_2_START,
                    scrollWindowEnd: FRAME_7_ADJUST_2_END,
                    opacity: 0.2,
                    size: 150
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_3_START,
                    scrollWindowEnd: FRAME_7_ADJUST_3_END,
                    opacity: 0.05,
                    size: 1
                }
            ]
        },
        {
            id: "frame-7-avatar-lime-bottom",
            x: 250,
            y: 91.8,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "lime400",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N522.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 76.5,
                    y: 91.8,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-pill-release",
            x: 300,
            y: 92.5,
            shape: "rectangle",
            size: PILL_NORMAL_SIZE,
            bgColor: "black",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N50J.svg",
            imagePlacement: "insideLeading",
            borderColor: "lime500",
            textColor: "white",
            text: intl.formatMessage({
                id: "teamworkGraph.frame7.release",
                defaultMessage: "Release"
            }),
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 92.8,
                    y: 92.5,
                    size: PILL_SHRUNKEN_SIZE
                }
            ]
        },
        {
            id: "frame-7-behind-avatar-yellow",
            x: 150,
            y: 99,
            shape: "circle",
            size: 1,
            opacity: 0,
            bgColor: "yellow700",
            borderColor: "yellow400",
            borderWidth: 8,
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 47.5,
                    y: 99
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_1_START,
                    scrollWindowEnd: FRAME_7_ADJUST_1_END,
                    opacity: 0.2,
                    size: 150
                },
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: FRAME_7_ADJUST_3_START,
                    scrollWindowEnd: FRAME_7_ADJUST_3_END,
                    opacity: 0.05,
                    size: 1
                }
            ]
        },
        {
            id: "frame-7-avatar-yellow",
            x: 150,
            y: 99,
            shape: "circle",
            size: AVATAR_NORMAL_SIZE,
            bgColor: "yellow400",
            bgImage: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N51Y.png",
            animationPhases: [
                {
                    delay: 0,
                    duration: 0,
                    scrollWindowStart: TRANSITION_FROM_FRAME_6_TO_7_START,
                    scrollWindowEnd: TRANSITION_FROM_FRAME_6_TO_7_END,
                    x: 47.5,
                    y: 99,
                    size: AVATAR_SHRUNKEN_SIZE
                }
            ]
        }
    ];

    const sectionRef = useRef<HTMLDivElement>(null);
    const desktopShellRef = useRef<HTMLDivElement>(null);
    const skipTargetRef = useRef<HTMLDivElement>(null);
    const skipScrollRafRef = useRef(0);
    const skipScrollMountedRef = useRef(true);
    const skipAnimatingRef = useRef(false);
    const hasEverFullyScrolledPastSectionRef = useRef(false);
    const prevDesktopScrollPhaseRef = useRef<DesktopScrollPhase>("track");

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [percentageScrolled, setPercentageScrolled] = useState(0);
    const [hasScrolledPast, setHasScrolledPast] = useState(false);
    const [hasScrolledAboveAgain, setHasScrolledAboveAgain] = useState(false);
    const [desktopScrollPhase, setDesktopScrollPhase] =
        useState<DesktopScrollPhase>("track");
    /** Holds the graph at final scroll progress (same as natural dock) while skipping. */
    const [skipEndFrameHold, setSkipEndFrameHold] = useState(false);
    const [skipScrollAnimating, setSkipScrollAnimating] = useState(false);

    useEffect(() => {
        if (
            prevDesktopScrollPhaseRef.current === "docked" &&
            desktopScrollPhase === "track"
        ) {
            hasEverFullyScrolledPastSectionRef.current = false;
        }
        prevDesktopScrollPhaseRef.current = desktopScrollPhase;
    }, [desktopScrollPhase]);

    useEffect(() => {
        if (hasScrolledPast && skipEndFrameHold) {
            setSkipEndFrameHold(false);
        }
    }, [hasScrolledPast, skipEndFrameHold]);

    useEffect(() => {
        skipScrollMountedRef.current = true;
        return () => {
            skipScrollMountedRef.current = false;
            if (skipScrollRafRef.current !== 0) {
                cancelAnimationFrame(skipScrollRafRef.current);
                skipScrollRafRef.current = 0;
            }
        };
    }, []);

    const handleDesktopSkipClick = useCallback(
        (e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "linkButton",
                actionSubjectId: "skipTeamworkGraph",
                source: "teamworkGraph"
            }).fire();
            if (skipAnimatingRef.current) {
                return;
            }
            const target = skipTargetRef.current;
            if (!target) {
                return;
            }

            skipAnimatingRef.current = true;
            setSkipScrollAnimating(true);
            setSkipEndFrameHold(true);

            const startY = window.scrollY;
            const endY = Math.max(
                0,
                Math.round(
                    target.getBoundingClientRect().top +
                        window.scrollY -
                        TEAMWORK_GRAPH_SKIP_SCROLL_TOP_PADDING_PX
                )
            );
            const prefersReducedMotion = window.matchMedia(
                "(prefers-reduced-motion: reduce)"
            ).matches;

            if (prefersReducedMotion || Math.abs(endY - startY) < 2) {
                window.scrollTo(0, endY);
                skipAnimatingRef.current = false;
                setSkipScrollAnimating(false);
                target.focus({ preventScroll: true });
                return;
            }

            const t0 = performance.now();
            const duration = TEAMWORK_GRAPH_SKIP_SCROLL_MS;

            const step = (now: number) => {
                if (!skipScrollMountedRef.current) {
                    skipAnimatingRef.current = false;
                    setSkipScrollAnimating(false);
                    return;
                }
                const t = Math.min(1, (now - t0) / duration);
                const y =
                    startY + (endY - startY) * easeInOutCubicSkipScroll(t);
                window.scrollTo(0, y);
                if (t < 1) {
                    skipScrollRafRef.current = requestAnimationFrame(step);
                } else {
                    skipScrollRafRef.current = 0;
                    skipAnimatingRef.current = false;
                    setSkipScrollAnimating(false);
                    target.focus({ preventScroll: true });
                }
            };

            skipScrollRafRef.current = requestAnimationFrame(step);
        },
        [createAnalyticsEvent]
    );

    useEffect(() => {
        const section = sectionRef.current;
        if (!section) {
            return;
        }

        const updateSectionScrollState = () => {
            const el = sectionRef.current;
            if (!el) {
                return;
            }
            const rect = el.getBoundingClientRect();
            const fullyPast = rect.bottom <= 0;

            setPercentageScrolled(((rect.top * -1) / rect.height) * 100);

            setHasScrolledPast(fullyPast);
            if (fullyPast) {
                hasEverFullyScrolledPastSectionRef.current = true;
            }
            setHasScrolledAboveAgain(
                hasEverFullyScrolledPastSectionRef.current && !fullyPast
            );

            setDesktopScrollPhase((phase) => {
                if (phase === "track") {
                    return fullyPast ? "docked" : "track";
                }
                const shell = desktopShellRef.current;
                if (shell) {
                    const shellTop = shell.getBoundingClientRect().top;
                    if (shellTop > 6) {
                        return "track";
                    }
                }
                return "docked";
            });
        };

        updateSectionScrollState();

        window.addEventListener("scroll", updateSectionScrollState, {
            passive: true
        });
        window.addEventListener("resize", updateSectionScrollState);

        const ro = new ResizeObserver(updateSectionScrollState);
        ro.observe(section);
        const shell = desktopShellRef.current;
        if (shell) {
            ro.observe(shell);
        }

        return () => {
            window.removeEventListener("scroll", updateSectionScrollState);
            window.removeEventListener("resize", updateSectionScrollState);
            ro.disconnect();
        };
    }, []);

    return (
        <div
            style={{
                margin: "0",
                position: "relative"
            }}
        >
            <div
                className={`desktop_container_${desktopScrollPhase} scroll_past_${hasScrolledPast} scroll_above_again_${hasScrolledAboveAgain}`}
                ref={desktopShellRef}
                css={[teamworkGraphDesktopOnlyStyles]}
            >
                <div
                    className={`tall_height_${desktopScrollPhase}`}
                    ref={sectionRef}
                    style={{
                        position: "relative",
                        minHeight: `${stickyHeight}vh`
                    }}
                >
                    <div
                        className={`sticky_${desktopScrollPhase}`}
                        css={[stickyWrapperCss]}
                    >
                        <SpiderWebContainer
                            // scrollMagnetAnchors={orderedScrollMagnetAnchors}
                            // scrollMagnetMinProgressDelta={
                            //     scrollMagnetMinProgressDelta
                            // }
                            // scrollMagnetDirectionBias={
                            //     scrollMagnetDirectionBias
                            // }
                            bgColor="black"
                            animationPhasesDrive="scroll"
                            scrollSectionRef={sectionRef}
                            scrollPhaseProgressOverride={
                                desktopScrollPhase === "docked" ||
                                skipEndFrameHold
                                    ? 1
                                    : null
                            }
                            onScrollPhaseProgressChange={
                                onDesktopScrollPhaseProgressChange
                            }
                            nodes={teamworkGraphAllFramesNodes}
                            connectors={teamworkGraphAllFramesConnectors}
                            useOnlyExplicitConnectors={true}
                            connectorColor="blue300"
                            connectorWidth={3}
                            replayOnVisible={false}
                            useViewportDimensions={true}
                            fontFamily="monospace"
                            animateOnLoad={false}
                        />
                        <div css={gradientOverlayStyle} />

                        <div
                            className="navigation-container"
                            css={navigationContainerCss}
                        >
                            <div
                                className="progress-timeline"
                                css={progressTimelineCss}
                            >
                                <div
                                    className="bars behind"
                                    css={[barsCss, barsBehindCss]}
                                />
                                <div
                                    style={{
                                        clipPath: `polygon(0 0, ${percentageScrolled}% 0, ${percentageScrolled}% 100%, 0 100%)`
                                    }}
                                    className="bars front"
                                    css={[barsCss, barsFrontCss]}
                                />
                            </div>
                            <div className="skip-button-area">
                                <a
                                    className="skip-button"
                                    css={[
                                        skipButtonCss,
                                        skipScrollAnimating &&
                                            css({
                                                pointerEvents: "none",
                                                opacity: 0.65
                                            })
                                    ]}
                                    href="#skip-graph"
                                    aria-busy={skipScrollAnimating}
                                    aria-disabled={skipScrollAnimating}
                                    onClick={handleDesktopSkipClick}
                                >
                                    {intl.formatMessage({
                                        id: "teamworkGraph.skipButton",
                                        defaultMessage: "Skip"
                                    })}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div css={teamworkGraphMobileOnlyStyles}>
                <div style={{ width: "100%", margin: "0 auto" }}>
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesA}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsA}
                        connectorColor="blue600"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(537)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesB}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsB}
                        connectorColor="blue600"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(705)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesC}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsC}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(330)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesD}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsD}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(550)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesE}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsE}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(330)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesF}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsF}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(550)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesG}
                        useOnlyExplicitConnectors={true}
                        connectors={[]}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(300)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesH}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsH}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(550)}
                    />
                    <SpiderWebContainer
                        bgColor="black"
                        nodes={teamworkGraphMobileNodesI}
                        useOnlyExplicitConnectors={true}
                        connectors={teamworkGraphMobileConnectorsI}
                        connectorColor="blue300"
                        connectorWidth={3}
                        width={MOBILE_TEAMWORK_GRAPH_WIDTH}
                        height={scaledMobileTeamworkGraphHeight(800)}
                    />
                </div>
            </div>
            <div
                ref={skipTargetRef}
                className="skip-to-bottom"
                id="skip-graph"
                tabIndex={-1}
            />
        </div>
    );
};

export default UniformTeamworkGraph;
